import { Minus, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { mainStart, type LayoutNode, type TreeLayout } from './layout';

/**
 * The chart's records and the lines between them, drawn from computed positions.
 *
 * **Absolute, not nested.** A node belongs in its own lane whatever its parent's is, and a
 * nested list can only ever place a child after its parent — so it cannot draw one that
 * belongs beside it or before it. Positions come from `computeLayout`; everything here reads
 * them and paints.
 *
 * A connector is drawn from the parent's own edges to the child's, so it always meets both
 * cards: there is no "roughly where the next row starts" left in the geometry.
 */
export function TreeBody<T>({
  layout,
  renderNode,
  vertical,
  collapsible,
  onToggle,
  label,
  metrics,
}: {
  layout: TreeLayout<T>;
  renderNode: (
    node: T,
    state: {
      expanded: boolean;
      hasChildren: boolean;
      /**
       * True when the node sits in a sub-tier of its lane — its parent holds the same rank.
       * Render it smaller: the size is what says this is a level inside the tier rather than
       * a peer of the card above it.
       */
      sub: boolean;
    },
  ) => ReactNode;
  vertical: boolean;
  collapsible: boolean;
  onToggle: (key: string) => void;
  label: string;
  metrics: Metrics;
}) {
  const { cardMain, cardCross, gutter, crossGap, subIndent, neck } = metrics;

  const crossPitch = cardCross + crossGap;
  const start = (entry: LayoutNode<T>) => mainStart(entry, layout, subIndent);
  /*
    Half a sibling gap of clearance before the first slot, for the reason the lanes take one:
    slot 0 at zero put the first card flush under the sticky header — the one row that touched
    a border. The extent already allows for it, because a pitch carries a whole gap and the
    padding is half of one at each end.
  */
  const cross = (entry: LayoutNode<T>) => entry.slot * crossPitch + crossGap / 2;
  /** Where a card ends. Every card is `cardMain` long — the lane grew, the card did not. */
  const span = (entry: LayoutNode<T>) => start(entry) + cardMain;

  /*
    A sub-tier card is smaller on BOTH axes — the main axis by its inset, the cross axis by a
    step per level. Shrinking only along main would leave a card that is merely shifted, and
    the reader has to be able to see a sub-tier without measuring it against its neighbour.
  */
  const thickness = (entry: LayoutNode<T>) =>
    Math.max(cardCross - entry.depth * SUB_THINNING, cardCross / 2);

  /** A card's box, in the orientation's own terms. */
  const box = (entry: LayoutNode<T>) =>
    vertical
      ? { top: start(entry), left: cross(entry), height: cardMain, width: thickness(entry) }
      : { left: start(entry), top: cross(entry), width: cardMain, height: thickness(entry) };

  const lines: ReactNode[] = [];

  for (const entry of layout.placed) {
    if (entry.parentKey === null) {
      continue;
    }

    const parent = layout.byKey.get(entry.parentKey);

    if (!parent) {
      continue;
    }

    lines.push(...connector(parent, entry));
  }

  /**
   * The run between one card and another.
   *
   * **A run touches its own two cards and nothing else.** A line passing under a third card
   * reads as connecting to it — which is how a child came to look like a child of its own
   * sibling — so wherever cards sit between the two ends, the run goes around them through a
   * channel just outside their edge.
   *
   * A step back to an earlier lane draws backwards, and looks it. That is deliberate: it
   * means the hierarchy contradicts its own ranks, and hiding it would be worse.
   */
  function connector(parent: LayoutNode<T>, child: LayoutNode<T>): ReactNode[] {
    const key = `${parent.key}->${child.key}`;

    const pMain = start(parent);
    const cMain = start(child);
    const pEnd = span(parent);
    const cEnd = span(child);
    const pCross = cross(parent) + thickness(parent) / 2;
    const cCross = cross(child) + thickness(child) / 2;
    const forward = child.lane > parent.lane || (child.lane === parent.lane && cMain > pMain);

    /*
      The two cards line up on the cross axis, so the shortest run is straight along main,
      edge to edge — and it is the right one whenever nothing is standing in it.
    */
    if (Math.abs(pCross - cCross) < 1) {
      const from = forward ? pEnd : pMain;
      const to = forward ? cMain : cEnd;

      if (!crosses(pCross, from, to, [parent.key, child.key])) {
        return [line(`${key}-run`, { mainFrom: from, mainTo: to, crossAt: pCross })];
      }

      // Something is: out through the parent's own side, past it all, in through the child's.
      const lane = channelBeside(parent);
      const pMid = pMain + cardMain / 2;
      const cMid = cMain + cardMain / 2;

      return [
        line(`${key}-out`, {
          mainAt: pMid,
          crossFrom: cross(parent) + thickness(parent),
          crossTo: lane,
        }),
        line(`${key}-run`, { mainFrom: pMid, mainTo: cMid, crossAt: lane }),
        line(`${key}-in`, {
          mainAt: cMid,
          crossFrom: lane,
          crossTo: cross(child) + thickness(child),
        }),
      ];
    }

    /*
      Same lane, so the child opens a SUB-TIER inside it and is inset from its parent. The run
      drops in that inset — out of the parent’s leading edge, along the cross axis inside the
      channel the indent just made, and in through the child’s leading edge.

      The channel is the whole point. It sits inside the tier band, where no run between two
      lanes ever goes, so a sub-tier run can never be confused with one arriving from the rank
      before — which is exactly how a child at its parent’s own rank used to read as its
      sibling. It is also why the indent is worth its width: it is not decoration, it is where
      this line lives.
    */
    if (parent.lane === child.lane) {
      const at = cMain - subIndent / 2;

      return [
        line(`${key}-drop`, { mainAt: at, crossFrom: pCross, crossTo: cCross }),
        line(`${key}-in`, { mainFrom: at, mainTo: cMain, crossAt: cCross }),
      ];
    }

    /*
      A step to another lane turns in the GUTTER, on the boundary between the two — not a
      fixed distance out from the parent's card.

      A lane is only as thick as one card until something stacks inside it, and then it grows.
      Measuring the turn from the card put it inside the parent's own lane, crossing whatever
      was stacked below; the boundary is the one place that is always empty.
    */
    const boundary = forward
      ? layout.laneOffsets[parent.lane] + layout.laneSpans[parent.lane] + gutter / 2
      : layout.laneOffsets[parent.lane] - gutter / 2;
    // A card ends where its lane ends, whatever sub-tier it sits in.
    const outEdge = forward ? pEnd : pMain;
    const inEdge = forward ? cMain : cEnd;

    const segments: ReactNode[] = [];
    // Where the run is travelling on the cross axis when it reaches the boundary.
    let turnFrom = pCross;

    if (crosses(pCross, outEdge, boundary, [parent.key])) {
      const lane = channelBeside(parent);
      const pMid = pMain + cardMain / 2;

      segments.push(
        line(`${key}-out`, {
          mainAt: pMid,
          crossFrom: cross(parent) + thickness(parent),
          crossTo: lane,
        }),
        line(`${key}-down`, { mainFrom: pMid, mainTo: boundary, crossAt: lane }),
      );
      turnFrom = lane;
    } else {
      segments.push(line(`${key}-out`, { mainFrom: outEdge, mainTo: boundary, crossAt: pCross }));
    }

    if (crosses(cCross, boundary, inEdge, [child.key])) {
      const lane = channelBeside(child);
      const cMid = cMain + cardMain / 2;

      segments.push(
        line(`${key}-turn`, { mainAt: boundary, crossFrom: turnFrom, crossTo: lane }),
        line(`${key}-run`, { mainFrom: boundary, mainTo: cMid, crossAt: lane }),
        line(`${key}-in`, {
          mainAt: cMid,
          crossFrom: lane,
          crossTo: cross(child) + thickness(child),
        }),
      );
    } else {
      segments.push(
        line(`${key}-turn`, { mainAt: boundary, crossFrom: turnFrom, crossTo: cCross }),
        line(`${key}-in`, { mainFrom: boundary, mainTo: inEdge, crossAt: cCross }),
      );
    }

    return segments;
  }

  /**
   * Whether any card other than the run's own two stands across it.
   *
   * This is the whole test. A run is legal when it touches its two ends and nothing else, and
   * asking the placed cards directly is the only way to know — every rule of thumb that stood
   * in for it ("is anything stacked below the parent") was right about the case it was written
   * for and wrong about the next one.
   */
  function crosses(
    crossAt: number,
    mainFrom: number,
    mainTo: number,
    exclude: readonly string[],
  ): boolean {
    const from = Math.min(mainFrom, mainTo);
    const to = Math.max(mainFrom, mainTo);

    return layout.placed.some((other) => {
      if (exclude.includes(other.key)) {
        return false;
      }

      const at = cross(other);

      if (crossAt <= at || crossAt >= at + thickness(other)) {
        return false;
      }

      const main = start(other);

      return main < to && main + cardMain > from;
    });
  }

  /**
   * The clear line beside a card, for a run that has to get past something.
   *
   * It is on the card's **trailing** side, in the gap before the next column of siblings. On
   * the leading side the first column's channel lands at a negative offset — off the chart,
   * under the lane rail.
   */
  function channelBeside(entry: LayoutNode<T>): number {
    return cross(entry) + thickness(entry) + crossGap / 2;
  }

  /**
   * One dashed run, given in main/cross terms and turned into a box.
   *
   * A run along the main axis has a `crossAt`; one along the cross axis has a `mainAt`. A
   * zero-length run is dropped rather than drawn as a dot.
   */
  function line(
    key: string,
    spec:
      | { mainFrom: number; mainTo: number; crossAt: number }
      | { mainAt: number; crossFrom: number; crossTo: number },
  ): ReactNode {
    if ('crossAt' in spec) {
      const length = Math.abs(spec.mainTo - spec.mainFrom);

      if (length < 1) {
        return null;
      }

      const from = Math.min(spec.mainFrom, spec.mainTo);

      return (
        <span
          key={key}
          aria-hidden="true"
          className={`absolute ${vertical ? V_RUN : H_RUN}`}
          style={
            vertical
              ? { top: from, height: length, left: spec.crossAt }
              : { left: from, width: length, top: spec.crossAt }
          }
        />
      );
    }

    const length = Math.abs(spec.crossTo - spec.crossFrom);

    if (length < 1) {
      return null;
    }

    const from = Math.min(spec.crossFrom, spec.crossTo);

    return (
      <span
        key={key}
        aria-hidden="true"
        className={`absolute ${vertical ? H_RUN : V_RUN}`}
        style={
          vertical
            ? { left: from, width: length, top: spec.mainAt }
            : { top: from, height: length, left: spec.mainAt }
        }
      />
    );
  }

  const mainExtent =
    layout.laneOffsets[layout.laneSpans.length - 1] +
    layout.laneSpans[layout.laneSpans.length - 1] +
    gutter / 2;
  const crossExtent = layout.slots * crossPitch;

  return (
    <div
      className="relative"
      style={
        vertical
          ? { height: mainExtent, width: crossExtent }
          : { width: mainExtent, height: crossExtent }
      }
    >
      {lines}

      {/*
        A flat list, because the tree's shape is carried by the positions rather than by the
        markup. It still nests for a screen reader through `aria-level`, which is what the
        nesting was ever announcing.
      */}
      <ul aria-label={label} className="contents">
        {layout.placed.map((entry) => {
          const { top, left, width, height } = box(entry);
          const groupId = `tree-group-${entry.key}`;

          return (
            <li
              key={entry.key}
              className="absolute flex items-center"
              style={{ top, left, width, height }}
              aria-level={entry.lane + 1}
            >
              <div className="flex h-full w-full items-center">
                {renderNode(entry.node, {
                  expanded: entry.expanded,
                  hasChildren: entry.hasChildren,
                  sub: entry.depth > 0,
                })}
              </div>

              {/*
                The disclosure sits on the node's outgoing edge, on the run it opens — and it
                is centred across that edge so the line meets it whichever way the chart runs.
              */}
              {collapsible && entry.hasChildren ? (
                <button
                  type="button"
                  onClick={() => onToggle(entry.key)}
                  aria-expanded={entry.expanded}
                  aria-controls={groupId}
                  className="absolute z-10 flex h-5 w-5 items-center justify-center rounded-full border border-slate-500 bg-white text-slate-600 transition hover:border-slate-600 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  style={
                    vertical
                      ? { top: height + neck, left: width / 2 - 10 }
                      : { left: width + neck, top: height / 2 - 10 }
                  }
                >
                  {entry.expanded ? (
                    <Minus className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <Plus className="h-3 w-3" aria-hidden="true" />
                  )}
                  <span className="sr-only">
                    {entry.expanded ? 'Collapse' : 'Expand'} this branch
                  </span>
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export interface Metrics {
  /** A card's size along the axis the tree grows. */
  cardMain: number;
  /** A card's size along the axis siblings spread on. */
  cardCross: number;
  /** Between one lane and the next. */
  gutter: number;
  /** Between one sibling and the next. */
  crossGap: number;
  /** How far a sub-tier is inset inside its lane, taken out of the card. */
  subIndent: number;
  neck: number;
  circle: number;
  stem: number;
}

/**
 * How much thinner a card gets per sub-tier, on the axis siblings spread along.
 *
 * The main axis already shrinks by the indent; this is the other half, and it is what makes a
 * sub-tier visible at a glance rather than only in alignment.
 */
const SUB_THINNING = 6;

/*
  Dashed, and the darkest of the chart's three line tones — slate-500, against slate-400 for a
  card's border and slate-200 for a lane rule. A connector is a relationship between two
  records; a rule is chrome marking a lane; and the order between them is the hierarchy.

  A run is a zero-thickness box carrying one border, so a vertical one needs `w-0` and a
  horizontal one `h-0`.
*/
const V_RUN = 'w-0 border-l border-dashed border-slate-500';
const H_RUN = 'h-0 border-t border-dashed border-slate-500';
